import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, ArrowRight, MapPin, Handshake } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useStates from "@/lib/useStates";
import useCommodities from "@/lib/useCommodities";

export default function Listings() {
  const { user } = useAuth();
  const commodities = useCommodities();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterCommodity, setFilterCommodity] = useState("");
  const [showCreate, setShowCreate] = useState(params.get("create") === "1");

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filterType !== "all") q.set("listing_type", filterType);
      if (filterCommodity) q.set("commodity", filterCommodity);
      const { data } = await api.get(`/listings?${q.toString()}`);
      setRows(data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType, filterCommodity]);

  return (
    <div className="space-y-6" data-testid="listings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-stone-500">Marketplace</div>
          <div className="font-display text-2xl font-bold text-stone-900">Buy & Sell Listings</div>
        </div>
        <button
          onClick={() => { setShowCreate(true); params.set("create", "1"); setParams(params); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-bold shadow-md transition-colors"
          data-testid="open-create-listing-btn"
        >
          <Plus weight="bold" size={16} /> Create Listing
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-xl bg-white border border-stone-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-md border border-stone-200 overflow-hidden text-sm">
          {["all", "sell", "buy"].map((v) => (
            <button
              key={v} onClick={() => setFilterType(v)}
              className={`px-3 py-1.5 font-semibold ${filterType === v ? "bg-emerald-900 text-white" : "text-stone-700 hover:bg-stone-100"}`}
              data-testid={`filter-type-${v}`}
            >
              {v.toUpperCase()}
            </button>
          ))}
        </div>
        <select
          value={filterCommodity} onChange={(e) => setFilterCommodity(e.target.value)}
          className="px-3 py-2 rounded-md border border-stone-200 text-sm font-semibold bg-white text-stone-700"
          data-testid="filter-commodity"
        >
          <option value="">All commodities</option>
          {commodities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-xl bg-white border border-stone-200 h-56 shimmer" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl bg-white border border-stone-200 p-16 text-center text-stone-500">
          No listings match your filters.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((r) => <ListingCard key={r.id} row={r} isMine={r.user_id === user.id} onCreated={load} />)}
        </div>
      )}

      <AnimatePresence>
        {showCreate && <CreateListingModal prefill={Object.fromEntries(params)} onClose={() => { setShowCreate(false); params.delete("create"); setParams(params); }} onCreated={load} />}
      </AnimatePresence>
    </div>
  );
}

function ListingCard({ row, isMine, onCreated }) {
  const [busy, setBusy] = useState(false);
  const initiateDeal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/deals", {
        listing_id: row.id,
        agreed_price: row.price_expected,
        quantity: row.quantity,
      });
      toast.success(`Deal created for ${row.commodity}`);
      window.location.href = `/app/deals/${data.id}`;
    } catch (ex) {
      toast.error(formatErr(ex?.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="lift rounded-xl bg-white border border-stone-200 p-5" data-testid={`listing-card-${row.id}`}>
      <div className="flex items-center justify-between">
        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${row.listing_type === "sell" ? "bg-emerald-100 text-emerald-800" : "bg-orange-100 text-orange-800"}`}>
          {row.listing_type === "sell" ? "Selling" : "Buying"}
        </span>
        <span className="text-[10px] font-mono text-stone-400">#{row.id.slice(-6)}</span>
      </div>
      <div className="mt-3 font-display text-xl font-bold text-stone-900">{row.commodity}</div>
      <div className="mt-1 text-sm text-stone-600 font-mono">{row.quantity} {row.unit} · Grade {row.grade || "A"}</div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-display text-3xl font-black text-emerald-900">₹{row.price_expected.toLocaleString("en-IN")}</span>
        <span className="text-xs text-stone-500">/{row.unit}</span>
      </div>
      <div className="mt-3 flex items-center gap-1 text-xs text-stone-500">
        <MapPin size={12} /> {row.district}, {row.state} · {row.pincode}
      </div>
      <div className="mt-4 pt-3 border-t border-stone-100 flex items-center justify-between">
        <div className="text-xs">
          <div className="font-bold text-stone-800">{row.user_name}</div>
          <div className="text-stone-500 capitalize">{row.user_role}</div>
        </div>
        {!isMine && row.status === "open" && (
          <button
            disabled={busy} onClick={initiateDeal}
            className="inline-flex items-center gap-1 text-xs font-bold text-white bg-emerald-900 hover:bg-emerald-800 px-3 py-1.5 rounded-md transition-colors disabled:opacity-60"
            data-testid={`start-deal-${row.id}`}
          >
            <Handshake size={14} weight="bold" /> Deal
          </button>
        )}
        {isMine && <span className="text-[10px] font-bold uppercase text-orange-600">Your Listing</span>}
      </div>
    </motion.div>
  );
}

function CreateListingModal({ onClose, onCreated, prefill = {} }) {
  const { user } = useAuth();
  const states = useStates();
  const commodities = useCommodities();
  // the server says which listing types this role may post (a farmer sells, an exporter buys, the others do both)
  const types = user?.listing_types?.length ? user.listing_types : ["sell", "buy"];
  // the trade page opens this form with the commodity, quantity, state and a note already filled in
  const [f, setF] = useState({
    listing_type: types.includes(prefill.type) ? prefill.type : types[0],
    commodity: prefill.commodity || "Wheat", quantity: Number(prefill.qty) > 0 ? Number(prefill.qty) : 100, unit: "quintal",
    price_expected: Number(prefill.price) > 0 ? Number(prefill.price) : 2400,
    state: prefill.state || "", district: "", pincode: "", notes: (prefill.notes || "").slice(0, 500), grade: "A",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/listings", { ...f, quantity: Number(f.quantity), price_expected: Number(f.price_expected) });
      toast.success("Listing published!");
      onCreated?.();
      onClose();
    } catch (ex) {
      toast.error(formatErr(ex?.response?.data?.detail));
    }
    setBusy(false);
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" data-testid="create-listing-modal">
      <motion.form
        initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }}
        onSubmit={submit}
        className="w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-stone-200 p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-orange-600">New Listing</div>
            <div className="font-display text-2xl font-bold text-stone-900">Publish to India Mandi</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-stone-100 rounded-md" data-testid="close-modal-btn"><X size={20} /></button>
        </div>

        <div className={`grid ${types.length > 1 ? "grid-cols-2" : "grid-cols-1"} gap-2 mb-4`}>
          {types.map((tp) => (
            <button key={tp} type="button" onClick={() => set("listing_type", tp)}
              className={`px-4 py-3 rounded-md border font-bold transition-colors ${f.listing_type === tp ? (tp === "sell" ? "bg-emerald-900 text-white border-emerald-900" : "bg-orange-600 text-white border-orange-600") : "border-stone-200 hover:border-stone-300"}`}
              data-testid={`type-${tp}`}
            >
              I want to {tp.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Commodity" input={
            <>
              <input required list="commodity-list" value={f.commodity} onChange={(e) => set("commodity", e.target.value)} className="w-full px-3 py-2 rounded-md border border-stone-300" data-testid="input-commodity" />
              <datalist id="commodity-list">{commodities.map((c) => <option key={c} value={c} />)}</datalist>
            </>
          } />
          <Field label="Grade" input={<input value={f.grade} onChange={(e) => set("grade", e.target.value)} className="w-full px-3 py-2 rounded-md border border-stone-300" data-testid="input-grade" />} />
          <Field label="Quantity (quintals)" input={<input type="number" min="1" value={f.quantity} onChange={(e) => set("quantity", e.target.value)} className="w-full px-3 py-2 rounded-md border border-stone-300" data-testid="input-qty" />} />
          <Field label="Price ₹/quintal" input={<input type="number" min="1" value={f.price_expected} onChange={(e) => set("price_expected", e.target.value)} className="w-full px-3 py-2 rounded-md border border-stone-300" data-testid="input-price" />} />
          <Field label="State" input={
            <select required value={f.state} onChange={(e) => set("state", e.target.value)} className="w-full px-3 py-2 rounded-md border border-stone-300 bg-white" data-testid="input-state">
              <option value="">Choose a state</option>
              {states.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>} />
          <Field label="District" input={<input required value={f.district} onChange={(e) => set("district", e.target.value)} className="w-full px-3 py-2 rounded-md border border-stone-300" data-testid="input-district" />} />
          <Field label="PIN Code" input={<input required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} title="6 digits" value={f.pincode} onChange={(e) => set("pincode", e.target.value)} className="w-full px-3 py-2 rounded-md border border-stone-300" data-testid="input-pincode" />} />
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Notes</label>
            <textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-stone-300" data-testid="input-notes" />
          </div>
        </div>

        <button
          type="submit" disabled={busy}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-bold transition-colors disabled:opacity-60"
          data-testid="submit-listing-btn"
        >
          {busy ? "Publishing…" : "Publish Listing"} <ArrowRight size={16} weight="bold" />
        </button>
      </motion.form>
    </motion.div>
  );
}

function Field({ label, input }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{label}</label>
      <div className="mt-1">{input}</div>
    </div>
  );
}
